/**
 * React hooks for realtime updates
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import type { FileEvent, PresenceEvent, UploadProgressEvent, OperationProgressEvent, OrgEvent, KeybookEvent } from '../core/events.js'
import { devOfflineState } from './devOffline.js'
import { dispatchDbOperationEnded } from './db-operations.js'
import { attachOfficeDocSender, onOfficeDocSubscribed, onOfficeDocEvent, isFollowedOfficeDoc } from './office-docs.js'
import { armConnectTimeout, createReconnectController, type ReconnectController } from '@papan/reconnect'
import { currentAccount, currentSessionSignal, deriveApiOrigin, deriveWsOrigin, endInFlightGetWindow, markResyncBoundary, sessionEndedSince, sessionFetch } from '@papan/api-client'

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

export interface NotificationPayload {
  id: string
  type: string
  title: string
  body?: string
  data?: Record<string, unknown>
  createdAt: string
}

export interface KeybookTeamPendingEvent {
  type: 'keybook:team-pending'
  tenantId: string
}

/**
 * The consolidated permission-change signal, forwarded to SPA clients from the
 * WS server's `subscribeToPermissionChanged` handler. Carries only the
 * non-sensitive scope + affected folder (never member emails). Consumers
 * (e.g. the drive access bar) refetch on it; the payload is a routing hint,
 * not authoritative state.
 */
export interface PermissionChangedSignal {
  type: 'permission:changed'
  scope?: 'org' | 'folder' | 'share' | 'team'
  affectedFolderId?: string
  /** `false` = the change names another member, not this viewer (#1259) — `changesViewerAccess`. */
  affectsViewer?: boolean
}

export interface RealtimeContextValue {
  status: ConnectionStatus
  userId: string | null
  presence: Record<string, { userName: string; location?: string; status?: string }>
  subscribe: (path: string) => void
  unsubscribe: (path: string) => void
  updatePresence: (location?: string, status?: 'active' | 'idle' | 'away') => void
  onFileEvent: (handler: (event: FileEvent) => void) => () => void
  onPresenceEvent: (handler: (event: PresenceEvent) => void) => () => void
  onUploadEvent: (handler: (event: UploadProgressEvent) => void) => () => void
  onOperationEvent: (handler: (event: OperationProgressEvent) => void) => () => void
  onOrgEvent: (handler: (event: OrgEvent) => void) => () => void
  /**
   * A detached `scripts.run` reached a terminal state (#471).
   *
   * The frame carries the job id and nothing else — deliberately. It says "ask
   * again now", never "here is the answer", so a run's output never travels on
   * the socket and the poll route stays the one place ownership is checked.
   */
  onScriptRun: (handler: (event: { jobId: string; state: 'completed' | 'failed' }) => void) => () => void
  /** Consolidated permission-change signal (grants / roles / teams / restricted
   *  toggle / share-links / outbound shares) — fire-and-refetch for perm-reactive
   *  UI like the drive access bar. */
  onPermissionChanged: (handler: (event: PermissionChangedSignal) => void) => () => void
  onNotification: (handler: (notification: NotificationPayload) => void) => () => void
  /** Keybook auto-onboarding nudge: a pending wrap may now be issuable. */
  onKeybookTeamPending: (handler: (event: KeybookTeamPendingEvent) => void) => () => void
  /** Keybook entry mutated (created / updated / deleted / shared / transferred).
   *  Subscribers usually want to refresh their visible list. */
  onKeybookEvent: (handler: (event: KeybookEvent) => void) => () => void
  /**
   * Phase A.3 resync signal. Fires when the server tells the client it
   * missed file events during a WS gap (reconnect, server restart, Redis
   * flush). Consumers should invalidate their relevant caches and refetch
   * — `useFiles` calls `refresh()` to pull the current list.
   */
  onResync: (handler: (kind: 'file') => void) => () => void
  /**
   * The FIRST connect's baseline (`sync-ack`): this tab had no watermark, so the
   * server handed back its current counter and nothing is reported as missed. But
   * anything the page read BEFORE this socket was subscribed — an open file's name,
   * say — may already be older than that baseline: an event in between is absorbed
   * by it and never delivered. A view holding ONE thing it loaded before the socket
   * came up re-reads it here — on its own request: the API client's in-flight GET
   * window ends first (#1210), so the re-read never joins a GET sent before the
   * socket subscribed. Lists deliberately don't re-read (see `sync-ack` below).
   */
  onBaseline: (handler: () => void) => () => void
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null)

interface RealtimeProviderProps {
  children: ReactNode
  wsUrl?: string
  tenantId?: string
  /**
   * #501 — whether realtime may connect. Defaults true. Passed `false` once the
   * session is confirmed dead so the reconnect loop stops churning instead of
   * masking the logout. Existing callers omit it and are unaffected.
   */
  enabled?: boolean
}

// #189 follow-up — BOUND the generic reconnect so a stuck tab can't storm the WS
// endpoint. Each reconnect is a fresh `/ws` upgrade, and on the CF-Pages topology
// every upgrade is a Worker invocation — the old fixed-3s reconnect ran ~20×/min
// forever for one bad tab (an observed CF-quota incident). The policy — exponential
// backoff to a ceiling, reset ONLY after a connection proves stable, give up after a
// cap — is the shared @papan/reconnect controller. The stable-gate resets on `onopen`
// here (this socket has no separate post-handshake ack). The 4001 auth-retry and 4008
// cap keep their own dedicated handling below and never touch the controller.

export function RealtimeProvider({
  children,
  wsUrl,
  tenantId: tenantIdProp,
  enabled = true,
}: RealtimeProviderProps) {
  const [status, setStatus] = useState<ConnectionStatus>('connecting')
  const [connectedUserId, setConnectedUserId] = useState<string | null>(null)
  const [presence, setPresence] = useState<Record<string, { userName: string; location?: string; status?: string }>>({})
  const devOfflineRef = useRef(false)

  const wsRef = useRef<WebSocket | null>(null)
  const fileEventHandlers = useRef<Set<(event: FileEvent) => void>>(new Set())
  const presenceEventHandlers = useRef<Set<(event: PresenceEvent) => void>>(new Set())
  const uploadEventHandlers = useRef<Set<(event: UploadProgressEvent) => void>>(new Set())
  const operationEventHandlers = useRef<Set<(event: OperationProgressEvent) => void>>(new Set())
  const orgEventHandlers = useRef<Set<(event: OrgEvent) => void>>(new Set())
  const scriptRunHandlers = useRef<Set<(event: { jobId: string; state: 'completed' | 'failed' }) => void>>(new Set())
  const permissionChangedHandlers = useRef<Set<(event: PermissionChangedSignal) => void>>(new Set())
  const notificationHandlers = useRef<Set<(notification: NotificationPayload) => void>>(new Set())
  const keybookTeamPendingHandlers = useRef<Set<(event: KeybookTeamPendingEvent) => void>>(new Set())
  const keybookEventHandlers = useRef<Set<(event: KeybookEvent) => void>>(new Set())
  const resyncHandlers = useRef<Set<(kind: 'file') => void>>(new Set())
  const baselineHandlers = useRef<Set<() => void>>(new Set())
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Consecutive 4001 (auth-rejected) closes. A 4001 right after login is
  // almost always the transient cookie-not-yet-effective window (the same
  // race waitForAuthReady gates the REST side) — NOT a real logout. Giving
  // up permanently on the first 4001 leaves a freshly-signed-in tab with no
  // realtime until a manual reload (visible as missed share/revoke events).
  // Retry a bounded number of times on a short backoff to ride out the
  // window; only stop once it's clearly persistent (genuinely logged out).
  const authRejectCountRef = useRef(0)
  // #189 follow-up — bounded generic-reconnect policy, now the shared
  // @papan/reconnect controller. Backoff grows per failed attempt and resets only
  // after a STABLE connection (markConnected → stays open). One per provider
  // instance, stable across renders. `reconnectTimeoutRef` below is still used by
  // the separate 4001 auth-retry path.
  const reconnectRef = useRef<ReconnectController | null>(null)
  // Clears the live socket's handshake (connect) timeout — see armConnectTimeout.
  // A ref so every teardown path (closeSocket: supersede, unmount, disable,
  // dev-offline) can clear it, not just the socket's own onopen/onclose.
  const clearConnectTimeoutRef = useRef<(() => void) | null>(null)
  if (!reconnectRef.current) {
    reconnectRef.current = createReconnectController({
      baseMs: 1_000,
      ceilingMs: 30_000,
      stableAfterMs: 10_000,
      maxAttempts: 60,
      log: (msg) => console.log(`[Realtime ${new Date().toISOString()}] ${msg}`),
      onGiveUp: (attempts) => {
        console.warn(`[Realtime ${new Date().toISOString()}] ${attempts} reconnect attempts could not stabilize — giving up (reload to retry)`)
        setStatus('error')
      },
    })
  }
  // #2 (WS-off-Pages-Function) — monotonic connect generation. The token path
  // awaits a mint before opening the socket; if a newer connect()/unmount ran
  // during that await, the stale attempt must abort instead of opening a zombie WS.
  const connectGenRef = useRef(0)
  // Phase A.3: highest file-event seq seen for the active tenant. Persisted
  // in sessionStorage (per tab) so an in-tab reload keeps the resync window
  // tight; cross-tab consistency intentionally NOT shared (sessionStorage
  // is tab-local) since two tabs picking different `lastSeen` values is
  // safer than one tab clobbering the other.
  const lastSeenFileSeqRef = useRef<number | null>(null)

  const tenantIdRef = useRef(tenantIdProp)
  tenantIdRef.current = tenantIdProp

  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  /** sessionStorage key for the per-tab `lastSeenFileSeq` of a tenant. */
  function fileSeqStorageKey(tid: string): string {
    return `papan:lastSeenFileSeq:${tid}`
  }

  /**
   * Hydrate `lastSeenFileSeqRef` from sessionStorage for the current tenant.
   * Called on connect (and tenant switch) so a quick in-tab reload keeps
   * the resync window tight rather than starting from zero.
   */
  const hydrateLastSeenFileSeq = useCallback(() => {
    const tid = tenantIdRef.current
    if (!tid || typeof window === 'undefined') {
      lastSeenFileSeqRef.current = null
      return
    }
    try {
      const raw = window.sessionStorage.getItem(fileSeqStorageKey(tid))
      lastSeenFileSeqRef.current = raw ? parseInt(raw, 10) : null
    } catch {
      lastSeenFileSeqRef.current = null
    }
  }, [])

  /** Persist the current `lastSeenFileSeqRef` to sessionStorage. */
  const persistLastSeenFileSeq = useCallback(() => {
    const tid = tenantIdRef.current
    if (!tid || typeof window === 'undefined') return
    try {
      const v = lastSeenFileSeqRef.current
      if (v === null) window.sessionStorage.removeItem(fileSeqStorageKey(tid))
      else window.sessionStorage.setItem(fileSeqStorageKey(tid), String(v))
    } catch {
      // sessionStorage can throw in private mode; tolerate.
    }
  }, [])

  // #501 — shared teardown fragments, byte-identical across the sites that had
  // them inlined (connect()'s disabled guard, connect()'s close-existing branch,
  // the devOffline branch, the unmount cleanup, and the enabled-transition
  // effect). Each caller still adds its own connectGenRef bump / setStatus where
  // it needs them — those differ by site, so only the common parts are extracted.
  // Stable (useCallback [], refs + the stable setStatus only), so callers may
  // list them in dep arrays with no re-render/reconnect churn.
  const closeSocket = useCallback(() => {
    // Null onclose BEFORE close() so the socket's async onclose can't schedule a
    // reconnect that stomps the next connect (e.g. a rapid offline→online toggle,
    // #222; and a StrictMode mount→unmount→remount leaking a phantom WS).
    clearConnectTimeoutRef.current?.()
    clearConnectTimeoutRef.current = null
    if (wsRef.current) {
      wsRef.current.onclose = null
      wsRef.current.close()
      wsRef.current = null
      // onclose is dropped above, so detach the office-doc sender here (its subscriptions
      // die with this socket; a stale sender would count frames it never sends).
      attachOfficeDocSender(null)
    }
  }, [])
  const cancelReconnect = useCallback(() => {
    // Cancels BOTH pending reconnects: the generic @papan/reconnect controller
    // and the separate 4001 auth-retry timer.
    reconnectRef.current?.cancel()
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  const connect = useCallback(async () => {
    // #501 — do not (re)connect while disabled (session gone). Tear down any
    // live socket and stop scheduling, same shape as the devOffline path.
    if (!enabledRef.current) {
      cancelReconnect()
      connectGenRef.current++ // abort any in-flight token mint, symmetry with the transition effect
      closeSocket()
      setStatus('disconnected')
      return
    }
    // Cancel any pending reconnect from a previous connection (both the generic
    // controller-scheduled one and the 4001 auth-retry timer). Give-up is
    // TERMINAL by design — we do NOT reset() the controller here: a socket that
    // exhausted its budget stays down (status='error') until the user hits the
    // reload affordance (DriveView's realtime indicator). A page reload is a
    // fresh provider → fresh controller, so that's the recovery path.
    cancelReconnect()
    // Close existing connection if any
    closeSocket()
    // Generation guard — the token path awaits a mint before opening the socket.
    // If a newer connect()/unmount bumps this during the await, the stale attempt
    // aborts instead of opening a zombie WS.
    const gen = ++connectGenRef.current

    // Bounded generic reconnect (#189 follow-up). Shared by `onclose` and the
    // token-mint-failure path so both back off + give up identically.
    const scheduleGenericReconnect = () => {
      if (devOfflineRef.current || !enabledRef.current) return
      reconnectRef.current?.schedule(() => {
        setStatus('connecting')
        connect()
      })
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    // API base (#234): VITE_API_URL build-time override → runtime hostname
    // derivation (one code path for all envs) → same-origin.
    const apiBase =
      ((typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || '') ||
      deriveApiOrigin(window.location.hostname)
    const apiHost = apiBase ? apiBase.replace(/^https?:\/\//, '').replace(/\/$/, '') : window.location.host
    const sameOriginHost = wsUrl || `${protocol}//${apiHost}`

    // #2 (WS-off-Pages-Function) — when a WS base resolves, authenticate the WS
    // with a short-lived, single-use SESSION ws-token (minted over the REST
    // path where the cookie works) and connect to that base — the API subdomain,
    // which is CF-proxied straight to origin, OFF the Pages Function. Resolution
    // (#234): VITE_WS_URL build-time override → runtime hostname derivation
    // (wss://<derived -api host>, so every deployed env — golink/stagetest/prod/
    // pr-N pages.dev — uses the ws-token path identically). Empty (localhost
    // dev, unknown hosts) → the legacy same-origin cookie path below.
    // NOTE: plain `import.meta.env.VITE_WS_URL` (NOT `?.`) — Vite only statically
    // inlines the dot form at build; the optional-chain form is left as a runtime
    // lookup that resolves to undefined in the production bundle (so VITE_WS_URL
    // silently never took effect).
    const wsBase =
      ((typeof import.meta !== 'undefined' && (import.meta.env.VITE_WS_URL as string | undefined)) || '') ||
      deriveWsOrigin(window.location.hostname)
    let url: string
    let openedTenant: string | undefined   // the tenant this socket was opened for
    if (wsBase) {
      let token: string
      // #1316: the mint belongs to the session that asked for it — a late answer is dropped, and nothing here retries it
      const mintSession = currentSessionSignal()
      const mintAccount = currentAccount()
      try {
        const res = await sessionFetch(`${apiBase}/api/realtime/session-ws-token`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        })
        if (!res.ok) throw new Error(`mint ${res.status}`)
        token = (await res.json())?.token
        if (!token) throw new Error('no token')
      } catch (error) {
        // #1316: the session ended with the mint in flight. Not a failed attempt — no backoff, nothing counted (a retry
        // of THIS mint would go out as the next session). The socket is keyed by the cookie and the tenant: after a
        // same-tenant identity switch neither changes (nor does `enabled`), so nothing else would reconnect — this
        // connect starts afresh, as the new session. Not after the ACCOUNT changed (sign-out, another user: the app's
        // account counter moved — `enabled` follows on its own), nor once a newer connect / unmount / disable bumped
        // the generation.
        if (sessionEndedSince(mintSession, error)) {
          if (connectGenRef.current === gen && currentAccount() === mintAccount && enabledRef.current) void connect()
          return
        }
        // Mint failed (offline / transient 5xx / auth lost) — treat like a failed
        // connect: back off + retry (bounded), never open a socket. Skip if a newer
        // connect superseded us mid-await.
        if (connectGenRef.current === gen) scheduleGenericReconnect()
        return
      }
      if (connectGenRef.current !== gen) return // superseded during the await
      // #1316: the tenant is read AFTER the mint. A tenant switch ends the session before the provider re-renders with
      // the new tenant, so the fresh connect above can start while the props still hold the old one; its mint lands
      // after the re-render, and the socket opens on the new tenant (the switch-tenant frame is sent only to an OPEN
      // socket, so one opened on the old tenant would stay there).
      const tid = tenantIdRef.current
      openedTenant = tid
      const q = new URLSearchParams({ ws_token: token })
      if (tid) q.set('tenant', tid)
      url = `${wsBase.replace(/\/$/, '')}/ws?${q.toString()}`
    } else {
      const tid = tenantIdRef.current
      openedTenant = tid
      url = tid ? `${sameOriginHost}/ws?tenant=${encodeURIComponent(tid)}` : `${sameOriginHost}/ws`
    }

    // #1350: every retry here is close-driven, so a constructor that THROWS (a blocked or invalid
    // connection attempt) would end reconnecting for good. Retry it like a failed token mint.
    let ws: WebSocket
    try {
      ws = new WebSocket(url)
    } catch (error) {
      console.warn(`[Realtime ${new Date().toISOString()}] opening the socket failed — retrying`, error)
      if (connectGenRef.current === gen) scheduleGenericReconnect()
      return
    }
    wsRef.current = ws
    // Bound the handshake: a CF edge stall leaves the socket in CONNECTING with no
    // open/error/close ever, and every retry here is close-driven — so without
    // this the tab sits on 'connecting' forever. The timeout close()s the socket;
    // the browser then fires onclose (1006) and the normal bounded reconnect below
    // retries it (same backoff + attempt cap, one close → one schedule).
    const clearConnectTimeout = armConnectTimeout(ws, {
      log: (msg) => console.warn(`[Realtime ${new Date().toISOString()}] ${msg}`),
    })
    clearConnectTimeoutRef.current = clearConnectTimeout

    let pingInterval: ReturnType<typeof setInterval> | null = null

    ws.onopen = () => {
      clearConnectTimeout()
      setStatus('connected')
      authRejectCountRef.current = 0 // a successful auth clears the 4001 streak
      console.log(`[Realtime ${new Date().toISOString()}] Connected`)

      // #189 follow-up — arm the stable-gate: the backoff/attempts reset only after
      // the connection proves STABLE (stays open stableAfterMs). A socket that drops
      // sooner (cap/auth/flap) cancels this via onDropped() in onclose, so the backoff
      // keeps growing instead of resetting to base every cycle.
      reconnectRef.current?.markConnected()

      // Keep-alive ping every 30s to prevent Cloudflare/proxy idle timeout (60-100s)
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }))
        }
      }, 30_000)

      // Phase A.3: send the resync handshake so the server can detect
      // events we missed while disconnected. `sync` is the only frame sent
      // in `onopen`.
      //
      // Sent UNCONDITIONALLY, including the first-ever connect where
      // `lastSeen` is null. That case can't have missed anything, so the
      // server answers `sync-ack` (no refetch) rather than `resync` — but it
      // does hand back a BASELINE. Skipping the frame entirely, as this did
      // until 2026-08-10, left a fresh tab with no watermark, so a socket
      // that flaps before the first file event arrives could never detect
      // the gap and the tab stayed silently stale until a manual refresh.
      hydrateLastSeenFileSeq()
      const nowTenant = tenantIdRef.current
      if (nowTenant && nowTenant !== openedTenant) {
        // #1316: the tenant changed while this socket was CONNECTING (the tenant effect only messages an OPEN socket) —
        // the same bundled switch-tenant frame the effect sends, in place of `sync` (two frames race, see below).
        const lastSeen = lastSeenFileSeqRef.current
        ws.send(JSON.stringify({ type: 'switch-tenant', tenantId: nowTenant, ...(lastSeen !== null ? { lastSeenFileSeq: lastSeen } : {}) }))
      } else {
        ws.send(JSON.stringify({ type: 'sync', lastSeenFileSeq: lastSeenFileSeqRef.current }))
      }

      // D5 — open office editors (re-)follow their documents on this socket; a RE-open
      // also tells each to re-check what it missed while disconnected (I28).
      attachOfficeDocSender((m) => { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m)) })
    }

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)

        switch (message.type) {
          case 'connected':
            setConnectedUserId(message.userId)
            setPresence(message.presence || {})
            break

          case 'file':
            // Phase A.3: track highest seq before fanning out so reconnect
            // sends an accurate `lastSeenFileSeq`. Defensive `>` guards
            // against out-of-order delivery (pub/sub is FIFO per channel
            // but we still don't want to regress the watermark).
            if (typeof message.event?.seq === 'number') {
              const prev = lastSeenFileSeqRef.current
              if (prev === null || message.event.seq > prev) {
                lastSeenFileSeqRef.current = message.event.seq
                persistLastSeenFileSeq()
              }
            }
            for (const handler of fileEventHandlers.current) {
              handler(message.event)
            }
            break

          case 'sync-ack':
            // Phase A.3 baseline. The server's answer to a `sync` that
            // carried no watermark: adopt its counter so the NEXT reconnect
            // has something to compare against. Deliberately does NOT call
            // the resync handlers — nothing was missed, and refetching every
            // list on every first connect would undo the whole point of
            // gating recovery on reconnects.
            if (typeof message.currentSeq === 'number') {
              lastSeenFileSeqRef.current = message.currentSeq
              persistLastSeenFileSeq()
            }
            // …but a view that read ONE thing before this socket was subscribed
            // may already be behind the baseline it just adopted (`onBaseline`).
            // #1210 (I2): its re-read must be its own — the in-flight GET window
            // ends first, so it never joins a GET sent before the subscription.
            // Only the window: no access-change bump here (owner, 2026-10-22 — a
            // bump on every first connect delayed ordinary deep-link opens).
            endInFlightGetWindow()
            for (const handler of baselineHandlers.current) {
              handler()
            }
            break

          case 'resync':
            // Phase A.3: server detected we missed file events during a
            // WS gap. Treat the recovery as the same shape as today's
            // refresh()-on-event — invalidate + refetch via the consumer's
            // registered handler. Update lastSeen to the server's current
            // seq so a follow-up reconnect within the same window doesn't
            // re-trigger.
            if (typeof message.currentSeq === 'number') {
              lastSeenFileSeqRef.current = message.currentSeq
              persistLastSeenFileSeq()
            }
            if (message.kind === 'file') {
              // #1210: anything asked before the reconnect may predate what the socket missed. Marked ONCE, here,
              // before any handler runs: the API client's in-flight GET window ends, and the file caches count it
              // as an access change (answers asked before it are asked again and seed nothing).
              markResyncBoundary()
              for (const handler of resyncHandlers.current) {
                handler('file')
              }
            }
            break

          case 'presence':
            for (const handler of presenceEventHandlers.current) {
              handler(message.event)
            }
            // Update local presence state
            type PresenceState = Record<string, { userName: string; location?: string; status?: string }>
            if (message.event.type === 'presence:leave') {
              setPresence((prev: PresenceState) => {
                const next = { ...prev }
                delete next[message.event.userId]
                return next
              })
            } else {
              setPresence((prev: PresenceState) => ({
                ...prev,
                [message.event.userId]: {
                  userName: message.event.userName,
                  location: message.event.location,
                  status: message.event.status,
                },
              }))
            }
            break

          case 'upload':
            for (const handler of uploadEventHandlers.current) {
              handler(message.event)
            }
            break

          case 'operation':
            for (const handler of operationEventHandlers.current) {
              handler(message.event)
            }
            break

          case 'script-run':
            for (const handler of scriptRunHandlers.current) {
              handler({ jobId: message.jobId as string, state: message.state as 'completed' | 'failed' })
            }
            break

          case 'oo-doc-subscribed':
            onOfficeDocSubscribed(message as { ref?: unknown; doc?: unknown; ok?: unknown })
            break

          case 'oo-doc':
            // D5 — a document's session event, for every editor following it (any org).
            onOfficeDocEvent(message as { doc?: unknown; event?: unknown })
            break

          case 'oo-conflict':
            // Collabora file was externally changed — dispatch for editor to handle
            // D5 — an editor following this document gets it from the document channel.
            if (isFollowedOfficeDoc(message.fileId)) break
            window.dispatchEvent(new CustomEvent('papan:oo-conflict', {
              detail: message,
            }))
            break

          case 'oo-dirty':
            // Collabora dirty state changed (save callback cleared it)
            // D5 — NOT dropped for a followed editor, unlike the others: a recipient's OWN
            // pointer flag clears only here (a clean close at pointer coords is no event of
            // the document's), and a second copy of a frame is harmless — it only resets.
            window.dispatchEvent(new CustomEvent('papan:oo-dirty', {
              detail: message,
            }))
            break

          case 'oo-discard':
            // Collabora changes discarded — all tabs should reload
            // D5 — an editor following this document gets it from the document channel.
            if (isFollowedOfficeDoc(message.fileId)) break
            window.dispatchEvent(new CustomEvent('papan:oo-discard', {
              detail: message,
            }))
            break

          case 'oo-resolve':
            // Collabora conflict resolved (keep mine) — dismiss banner on all tabs
            // D5 — an editor following this document gets it from the document channel.
            if (isFollowedOfficeDoc(message.fileId)) break
            window.dispatchEvent(new CustomEvent('papan:oo-resolve', {
              detail: message,
            }))
            break

          case 'org':
            for (const handler of orgEventHandlers.current) {
              handler(message.event)
            }
            break

          case 'permission:changed':
            for (const handler of permissionChangedHandlers.current) {
              handler(message as PermissionChangedSignal)
            }
            break

          case 'db-operation-ended':
            // #1101 — a background db operation of this person's ended; the file's DB editor shows the notice.
            dispatchDbOperationEnded(message.operation)
            break

          case 'notification':
            for (const handler of notificationHandlers.current) {
              handler(message.notification as NotificationPayload)
            }
            break

          case 'keybook:team-pending':
            for (const handler of keybookTeamPendingHandlers.current) {
              handler({ type: 'keybook:team-pending', tenantId: message.tenantId })
            }
            break

          case 'keybook:entry':
            for (const handler of keybookEventHandlers.current) {
              handler(message.event as KeybookEvent)
            }
            break

          case 'pong':
            // Heartbeat response
            break
        }
      } catch (err) {
        console.error(`[Realtime ${new Date().toISOString()}] Failed to parse message:`, err)
      }
    }

    ws.onclose = (event) => {
      attachOfficeDocSender(null)
      clearConnectTimeout()
      if (pingInterval) { clearInterval(pingInterval); pingInterval = null }
      wsRef.current = null
      // A connection that drops (for ANY reason) before it stabilized must NOT
      // reset the backoff — cancel the pending stable-timer. (#189 follow-up)
      reconnectRef.current?.onDropped()
      console.log(`[Realtime ${new Date().toISOString()}] Disconnected (code=${event.code}, reason=${event.reason || 'none'})`)

      // Auth rejection. Almost always the transient post-login window
      // (cookie not yet effective) rather than a real logout, so retry a
      // bounded number of times on a short backoff to ride it out. Only
      // give up once 4001 is clearly persistent — otherwise a freshly
      // signed-in tab loses realtime (missed share/revoke events) until a
      // manual reload. A genuinely logged-out client just burns these few
      // retries then stops.
      const MAX_AUTH_REJECTS = 5
      if (event.code === 4001) {
        authRejectCountRef.current += 1
        if (devOfflineRef.current || !enabledRef.current || authRejectCountRef.current > MAX_AUTH_REJECTS) {
          console.warn(`[Realtime ${new Date().toISOString()}] Auth rejected ${authRejectCountRef.current}x, giving up`)
          setStatus('error')
          return
        }
        console.warn(`[Realtime ${new Date().toISOString()}] Auth rejected (${authRejectCountRef.current}/${MAX_AUTH_REJECTS}), retrying`)
        setStatus('connecting')
        reconnectTimeoutRef.current = setTimeout(connect, 1000)
        return
      }

      // Per-user connection cap (server MAX_WS_CONNECTIONS_PER_USER). Reconnecting
      // would just be rejected 4008 again on every attempt forever (a storm that costs
      // the server an upgrade+auth+membership lookup per cycle). Treat as terminal —
      // surface 'error' and stop; closing another tab frees a slot and the user can
      // reload. (F22)
      if (event.code === 4008) {
        console.warn(`[Realtime ${new Date().toISOString()}] Too many connections (4008) — not reconnecting this tab`)
        setStatus('error')
        return
      }

      setStatus('disconnected')
      // #189 follow-up — bounded generic reconnect via the shared controller
      // (backoff → ceiling, then give up). Shared with the token-mint-failure
      // path so both behave identically.
      scheduleGenericReconnect()
    }

    ws.onerror = () => {
      setStatus('error')
    }
  }, [wsUrl, cancelReconnect, closeSocket])

  useEffect(() => {
    connect()

    // Test affordance: expose the live WebSocket so e2e can simulate a
    // transient disconnect (Phase A.3 resync). The provider's `onclose`
    // handler reconnects automatically (exponential backoff, ~1s for the
    // first attempt after a stable connection), so tests just need to
    // call `.close()`. Cheaper than building a debug UI.
    //
    // Always exposed (not gated to dev) because Phase A.3 must be
    // validated on cross-origin review envs (`pnpm test:e2e --target
    // pr-N`) per `e2e/CLAUDE.md`'s validate-on-review-env rule, and
    // those envs are built in production mode where Vite would drop
    // any `import.meta.env.DEV` conditional. The "DOS via .close()"
    // attack is not meaningful: closing the WS triggers one backed-off
    // reconnect that costs the same as any other HTTP request burst.
    if (typeof window !== 'undefined') {
      const w = window as unknown as { __papanRealtimeWs?: () => WebSocket | null }
      w.__papanRealtimeWs = () => wsRef.current
    }

    // Heartbeat every 30 seconds
    const heartbeat = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }))
      }
    }, 30000)

    // Dev offline: kill/reconnect realtime WS when toggled
    const unsubOffline = devOfflineState.subscribe((offline) => {
      devOfflineRef.current = offline
      if (offline) {
        cancelReconnect()
        closeSocket()
        setStatus('disconnected')
      } else {
        connect()
      }
    })

    return () => {
      clearInterval(heartbeat)
      unsubOffline()
      cancelReconnect()
      // Bump the connect generation so an in-flight token mint (token path)
      // that resolves after unmount aborts instead of opening a zombie WS.
      connectGenRef.current++
      // closeSocket() nulls onclose BEFORE close() so the auto-reconnect
      // setTimeout doesn't fire after this provider has unmounted (a dev
      // StrictMode mount → unmount → remount would otherwise leak a phantom WS).
      closeSocket()
    }
  }, [connect, cancelReconnect, closeSocket])

  // #501 — react to auth flipping mid-session. Guard on the PREVIOUS value so
  // the initial mount (and StrictMode's double-invoke) are no-ops; the mount
  // effect above already owns the first connect().
  const prevEnabledRef = useRef(enabled)
  useEffect(() => {
    const prev = prevEnabledRef.current
    prevEnabledRef.current = enabled
    if (prev === enabled) return
    if (!enabled) {
      cancelReconnect()
      connectGenRef.current++ // abort any in-flight token mint
      closeSocket()
      setStatus('disconnected')
    } else {
      void connect()
    }
  }, [enabled, connect, cancelReconnect, closeSocket])

  // Switch tenant via message (no reconnect) when tenantId prop changes
  const prevTenantRef = useRef(tenantIdProp)
  useEffect(() => {
    if (tenantIdProp && tenantIdProp !== prevTenantRef.current) {
      prevTenantRef.current = tenantIdProp
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        // Phase A.3: bundle `lastSeenFileSeq` for the NEW tenant into the
        // switch-tenant frame so the server can do the resync compare
        // atomically with the tenant mutation. Sending two separate
        // frames (switch-tenant, then sync) raced against the server's
        // async DB lookups — sync's `await getCurrentFileSeq` would land
        // before switch-tenant's `clientInfo.tenantId` write, comparing
        // the new tenant's lastSeen against the OLD tenant's counter.
        // One bundled frame makes the pre-mutation read of the post-
        // mutation tenant impossible by construction.
        hydrateLastSeenFileSeq()
        const lastSeen = lastSeenFileSeqRef.current
        wsRef.current.send(JSON.stringify({
          type: 'switch-tenant',
          tenantId: tenantIdProp,
          ...(lastSeen !== null ? { lastSeenFileSeq: lastSeen } : {}),
        }))
      }
    }
  }, [tenantIdProp, hydrateLastSeenFileSeq])

  const subscribe = useCallback((path: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', path }))
    }
  }, [])

  const unsubscribe = useCallback((path: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe', path }))
    }
  }, [])


  const updatePresence = useCallback((location?: string, presenceStatus?: 'active' | 'idle' | 'away') => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'presence:update',
        location,
        status: presenceStatus,
      }))
    }
  }, [])

  const onFileEvent = useCallback((handler: (event: FileEvent) => void) => {
    fileEventHandlers.current.add(handler)
    return () => {
      fileEventHandlers.current.delete(handler)
    }
  }, [])

  const onPresenceEvent = useCallback((handler: (event: PresenceEvent) => void) => {
    presenceEventHandlers.current.add(handler)
    return () => {
      presenceEventHandlers.current.delete(handler)
    }
  }, [])

  const onUploadEvent = useCallback((handler: (event: UploadProgressEvent) => void) => {
    uploadEventHandlers.current.add(handler)
    return () => {
      uploadEventHandlers.current.delete(handler)
    }
  }, [])

  const onOperationEvent = useCallback((handler: (event: OperationProgressEvent) => void) => {
    operationEventHandlers.current.add(handler)
    return () => {
      operationEventHandlers.current.delete(handler)
    }
  }, [])

  const onScriptRun = useCallback((handler: (event: { jobId: string; state: 'completed' | 'failed' }) => void) => {
    scriptRunHandlers.current.add(handler)
    return () => {
      scriptRunHandlers.current.delete(handler)
    }
  }, [])

  const onOrgEvent = useCallback((handler: (event: OrgEvent) => void) => {
    orgEventHandlers.current.add(handler)
    return () => {
      orgEventHandlers.current.delete(handler)
    }
  }, [])

  const onPermissionChanged = useCallback((handler: (event: PermissionChangedSignal) => void) => {
    permissionChangedHandlers.current.add(handler)
    return () => {
      permissionChangedHandlers.current.delete(handler)
    }
  }, [])

  const onNotification = useCallback((handler: (notification: NotificationPayload) => void) => {
    notificationHandlers.current.add(handler)
    return () => {
      notificationHandlers.current.delete(handler)
    }
  }, [])

  const onKeybookTeamPending = useCallback((handler: (event: KeybookTeamPendingEvent) => void) => {
    keybookTeamPendingHandlers.current.add(handler)
    return () => {
      keybookTeamPendingHandlers.current.delete(handler)
    }
  }, [])

  const onKeybookEvent = useCallback((handler: (event: KeybookEvent) => void) => {
    keybookEventHandlers.current.add(handler)
    return () => {
      keybookEventHandlers.current.delete(handler)
    }
  }, [])

  const onResync = useCallback((handler: (kind: 'file') => void) => {
    resyncHandlers.current.add(handler)
    return () => {
      resyncHandlers.current.delete(handler)
    }
  }, [])

  const onBaseline = useCallback((handler: () => void) => {
    baselineHandlers.current.add(handler)
    return () => {
      baselineHandlers.current.delete(handler)
    }
  }, [])

  const value: RealtimeContextValue = {
    status,
    userId: connectedUserId,
    presence,
    subscribe,
    unsubscribe,
    updatePresence,
    onFileEvent,
    onPresenceEvent,
    onUploadEvent,
    onOperationEvent,
    onScriptRun,
    onOrgEvent,
    onPermissionChanged,
    onNotification,
    onKeybookTeamPending,
    onKeybookEvent,
    onResync,
    onBaseline,
  }

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  )
}

export function useRealtime(): RealtimeContextValue {
  const context = useContext(RealtimeContext)
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider')
  }
  return context
}

/** Non-throwing variant of {@link useRealtime}: returns `null` when no
 *  RealtimeProvider is mounted above (e.g. a viewer rendered outside the app
 *  shell). Optional realtime consumers degrade gracefully instead of crashing
 *  the subtree. */
export function useRealtimeOptional(): RealtimeContextValue | null {
  return useContext(RealtimeContext)
}
